import {
  Badge,
  Box,
  Button,
  Card,
  Code,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  NativeSelect,
  Spinner,
  Table,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EditIcon,
  EyeIcon,
  EyeOffIcon,
  KeyIcon,
  PlusIcon,
  RefreshCwIcon,
  SearchIcon,
  Trash2Icon,
  XIcon,
} from 'lucide-react'
import { useState } from 'react'
import { k8sApi, type Secret, type CreateSecretRequest, type UpdateSecretRequest } from '~/lib/api'
import { toaster } from '~/toaster'

interface SecretsTabProps {
  namespace: string
  isAdmin: boolean
}

const PAGE_SIZE = 10

const SECRET_TYPES = [
  { value: 'Opaque', label: 'Opaque (generic)' },
  { value: 'kubernetes.io/tls', label: 'TLS' },
  { value: 'kubernetes.io/dockerconfigjson', label: 'Docker Config' },
  { value: 'kubernetes.io/basic-auth', label: 'Basic Auth' },
  { value: 'kubernetes.io/ssh-auth', label: 'SSH Auth' },
  { value: 'kubernetes.io/service-account-token', label: 'Service Account Token' },
]

// Key-Value Editor Component for Secrets
function SecretKeyValueEditor({
  data,
  onChange,
  readOnly = false,
  masked = true,
}: {
  data: Record<string, string>
  onChange?: (data: Record<string, string>) => void
  readOnly?: boolean
  masked?: boolean
}) {
  const entries = Object.entries(data)
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set())

  const toggleVisibility = (key: string) => {
    const newSet = new Set(visibleKeys)
    if (newSet.has(key)) {
      newSet.delete(key)
    } else {
      newSet.add(key)
    }
    setVisibleKeys(newSet)
  }

  const handleKeyChange = (oldKey: string, newKey: string) => {
    if (!onChange || readOnly) return
    const newData = { ...data }
    const value = newData[oldKey]
    delete newData[oldKey]
    newData[newKey] = value
    onChange(newData)
  }

  const handleValueChange = (key: string, value: string) => {
    if (!onChange || readOnly) return
    onChange({ ...data, [key]: value })
  }

  const handleAddEntry = () => {
    if (!onChange || readOnly) return
    let newKey = 'new-key'
    let counter = 1
    while (data[newKey]) {
      newKey = `new-key-${counter++}`
    }
    onChange({ ...data, [newKey]: '' })
  }

  const handleRemoveEntry = (key: string) => {
    if (!onChange || readOnly) return
    const newData = { ...data }
    delete newData[key]
    onChange(newData)
  }

  return (
    <VStack align="stretch" gap={3}>
      {entries.length === 0 ? (
        <Text color="content.secondary" fontSize="sm" fontStyle="italic">
          No data entries
        </Text>
      ) : (
        entries.map(([key, value]) => {
          const isVisible = !masked || visibleKeys.has(key)
          const displayValue = masked && !visibleKeys.has(key) ? '********' : value

          return (
            <HStack key={key} gap={2} align="start">
              <Input
                size="sm"
                fontFamily="mono"
                placeholder="Key"
                value={key}
                onChange={(e) => handleKeyChange(key, e.target.value)}
                readOnly={readOnly}
                w="200px"
                flexShrink={0}
              />
              <Textarea
                size="sm"
                fontFamily="mono"
                placeholder="Value"
                value={displayValue}
                onChange={(e) => handleValueChange(key, e.target.value)}
                readOnly={readOnly || masked}
                minH="36px"
                rows={1}
                resize="vertical"
              />
              {masked && (
                <IconButton
                  size="sm"
                  variant="ghost"
                  onClick={() => toggleVisibility(key)}
                  aria-label={isVisible ? 'Hide value' : 'Show value'}
                >
                  {isVisible ? <EyeOffIcon size={14} /> : <EyeIcon size={14} />}
                </IconButton>
              )}
              {!readOnly && !masked && (
                <IconButton
                  size="sm"
                  variant="ghost"
                  colorPalette="red"
                  onClick={() => handleRemoveEntry(key)}
                  aria-label="Remove entry"
                >
                  <Trash2Icon size={14} />
                </IconButton>
              )}
            </HStack>
          )
        })
      )}
      {!readOnly && !masked && (
        <Button size="sm" variant="outline" onClick={handleAddEntry} alignSelf="flex-start">
          <PlusIcon size={14} />
          Add Entry
        </Button>
      )}
    </VStack>
  )
}

// View Secret Modal
function ViewSecretModal({
  secret,
  isAdmin,
  onClose,
}: {
  secret: Secret
  isAdmin: boolean
  onClose: () => void
}) {
  const [revealed, setRevealed] = useState(false)
  const [revealedData, setRevealedData] = useState<Record<string, string> | null>(null)

  const revealMutation = useMutation({
    mutationFn: () => k8sApi.revealSecret(secret.namespace, secret.name),
    onSuccess: (data) => {
      setRevealedData(data.data)
      setRevealed(true)
      toaster.create({
        title: 'Secret revealed',
        description: 'Secret values are now visible',
        type: 'info',
      })
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to reveal secret',
        description: error.message,
        type: 'error',
      })
    },
  })

  const displayData = revealed && revealedData ? revealedData : secret.data

  return (
    <Box position="fixed" inset={0} bg="blackAlpha.600" zIndex={1000} onClick={onClose}>
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="800px"
        maxH="80vh"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderBottomWidth="1px"
          borderColor="border"
        >
          <VStack align="flex-start" gap={0}>
            <Heading size="md">Secret Details</Heading>
            <Text fontSize="sm" color="content.secondary">
              {secret.namespace}/{secret.name}
            </Text>
          </VStack>
          <HStack gap={2}>
            {isAdmin && !revealed && (
              <Button
                size="sm"
                colorPalette="orange"
                variant="outline"
                onClick={() => revealMutation.mutate()}
                loading={revealMutation.isPending}
              >
                <EyeIcon size={14} />
                Reveal Values
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close dialog">
              <XIcon size={16} />
            </Button>
          </HStack>
        </Flex>
        <Box overflow="auto" maxH="calc(80vh - 80px)" p={4}>
          <VStack align="stretch" gap={4}>
            <Box>
              <Text fontWeight="semibold" mb={2}>
                Metadata
              </Text>
              <HStack gap={4} flexWrap="wrap">
                <Box>
                  <Text fontSize="xs" color="content.secondary">
                    Name
                  </Text>
                  <Code>{secret.name}</Code>
                </Box>
                <Box>
                  <Text fontSize="xs" color="content.secondary">
                    Namespace
                  </Text>
                  <Code>{secret.namespace}</Code>
                </Box>
                <Box>
                  <Text fontSize="xs" color="content.secondary">
                    Type
                  </Text>
                  <Badge colorPalette="purple">{secret.type}</Badge>
                </Box>
                <Box>
                  <Text fontSize="xs" color="content.secondary">
                    Age
                  </Text>
                  <Text>{secret.age}</Text>
                </Box>
              </HStack>
            </Box>

            {secret.labels && Object.keys(secret.labels).length > 0 && (
              <Box>
                <Text fontWeight="semibold" mb={2}>
                  Labels
                </Text>
                <HStack gap={2} flexWrap="wrap">
                  {Object.entries(secret.labels).map(([k, v]) => (
                    <Badge key={k} variant="outline" fontFamily="mono">
                      {k}={v}
                    </Badge>
                  ))}
                </HStack>
              </Box>
            )}

            <Box>
              <HStack justify="space-between" mb={2}>
                <Text fontWeight="semibold">
                  Data ({Object.keys(displayData).length} keys)
                  {revealed && (
                    <Badge colorPalette="orange" ml={2}>
                      Revealed
                    </Badge>
                  )}
                </Text>
              </HStack>
              <SecretKeyValueEditor data={displayData} readOnly masked={!revealed} />
            </Box>
          </VStack>
        </Box>
      </Box>
    </Box>
  )
}

// Create/Edit Secret Modal
function SecretFormModal({
  secret,
  namespace,
  onClose,
  onSuccess,
}: {
  secret?: Secret
  namespace: string
  onClose: () => void
  onSuccess: () => void
}) {
  const isEdit = !!secret
  const [name, setName] = useState(secret?.name || '')
  const [type, setType] = useState(secret?.type || 'Opaque')
  const [data, setData] = useState<Record<string, string>>({})
  const [labels, setLabels] = useState<Record<string, string>>(secret?.labels || {})
  const [showLabels, setShowLabels] = useState(
    secret?.labels && Object.keys(secret.labels).length > 0,
  )

  const createMutation = useMutation({
    mutationFn: (req: CreateSecretRequest) => k8sApi.createSecret(namespace, req),
    onSuccess: () => {
      toaster.create({
        title: 'Secret created',
        description: `Secret "${name}" created successfully`,
        type: 'success',
      })
      onSuccess()
      onClose()
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to create Secret',
        description: error.message,
        type: 'error',
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: (req: UpdateSecretRequest) =>
      k8sApi.updateSecret(secret!.namespace, secret!.name, req),
    onSuccess: () => {
      toaster.create({
        title: 'Secret updated',
        description: `Secret "${secret!.name}" updated successfully`,
        type: 'success',
      })
      onSuccess()
      onClose()
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to update Secret',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleSubmit = () => {
    if (isEdit) {
      if (Object.keys(data).length === 0) {
        toaster.create({
          title: 'Data required',
          description: 'Please add at least one key-value pair',
          type: 'error',
        })
        return
      }
      updateMutation.mutate({
        data,
        labels: showLabels ? labels : undefined,
      })
    } else {
      if (!name.trim()) {
        toaster.create({
          title: 'Name required',
          description: 'Please enter a name for the Secret',
          type: 'error',
        })
        return
      }
      createMutation.mutate({
        name: name.trim(),
        type,
        data,
        labels: showLabels ? labels : undefined,
      })
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <Box position="fixed" inset={0} bg="blackAlpha.600" zIndex={1000} onClick={onClose}>
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="800px"
        maxH="80vh"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderBottomWidth="1px"
          borderColor="border"
        >
          <VStack align="flex-start" gap={0}>
            <Heading size="md">{isEdit ? 'Edit' : 'Create'} Secret</Heading>
            <Text fontSize="sm" color="content.secondary">
              {isEdit ? `${secret.namespace}/${secret.name}` : `Namespace: ${namespace}`}
            </Text>
          </VStack>
          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
            disabled={isPending}
            aria-label="Close dialog"
          >
            <XIcon size={16} />
          </Button>
        </Flex>
        <Box overflow="auto" maxH="calc(80vh - 140px)" p={4}>
          <VStack align="stretch" gap={4}>
            {!isEdit && (
              <>
                <Box>
                  <Text fontWeight="semibold" mb={2}>
                    Name *
                  </Text>
                  <Input
                    placeholder="my-secret"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fontFamily="mono"
                  />
                </Box>
                <Box>
                  <Text fontWeight="semibold" mb={2}>
                    Type
                  </Text>
                  <NativeSelect.Root>
                    <NativeSelect.Field value={type} onChange={(e) => setType(e.target.value)}>
                      {SECRET_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </NativeSelect.Field>
                  </NativeSelect.Root>
                </Box>
              </>
            )}

            {isEdit && (
              <Box p={3} bg="warning.subtle" borderRadius="md">
                <Text fontSize="sm" color="warning.fg">
                  Enter the new values for this secret. Existing values are not shown for security.
                </Text>
              </Box>
            )}

            <Box>
              <Text fontWeight="semibold" mb={2}>
                Data *
              </Text>
              <SecretKeyValueEditor data={data} onChange={setData} masked={false} />
            </Box>

            <Box>
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontWeight="semibold">Labels</Text>
                <Button size="xs" variant="ghost" onClick={() => setShowLabels(!showLabels)}>
                  {showLabels ? 'Hide' : 'Show'} Labels
                </Button>
              </Flex>
              {showLabels && (
                <SecretKeyValueEditor data={labels} onChange={setLabels} masked={false} />
              )}
            </Box>
          </VStack>
        </Box>
        <Flex justify="flex-end" gap={2} p={4} borderTopWidth="1px" borderColor="border">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button colorPalette="blue" onClick={handleSubmit} loading={isPending}>
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </Flex>
      </Box>
    </Box>
  )
}

export function SecretsTab({ namespace, isAdmin }: SecretsTabProps) {
  const queryClient = useQueryClient()
  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [viewSecret, setViewSecret] = useState<Secret | null>(null)
  const [editSecret, setEditSecret] = useState<Secret | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)

  // Fetch secrets
  const {
    data: secretsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['secrets', namespace],
    queryFn: () => k8sApi.secrets(namespace || undefined),
    retry: false,
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: ({ namespace, name }: { namespace: string; name: string }) =>
      k8sApi.deleteSecret(namespace, name),
    onSuccess: (_, variables) => {
      toaster.create({
        title: 'Secret deleted',
        description: `Secret "${variables.name}" deleted successfully`,
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['secrets'] })
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to delete Secret',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleDelete = (namespace: string, name: string) => {
    if (
      window.confirm(
        `Are you sure you want to delete Secret "${name}"?\n\nThis action cannot be undone and may break applications using this secret.`,
      )
    ) {
      deleteMutation.mutate({ namespace, name })
    }
  }

  const allSecrets = secretsData?.secrets || []

  // Filter based on search query
  const filteredSecrets = allSecrets.filter((s) => {
    if (!searchQuery.trim()) return true
    const query = searchQuery.toLowerCase()
    return (
      s.name.toLowerCase().includes(query) ||
      s.namespace.toLowerCase().includes(query) ||
      s.type.toLowerCase().includes(query) ||
      Object.keys(s.data).some((k) => k.toLowerCase().includes(query))
    )
  })

  // Pagination
  const totalPages = Math.ceil(filteredSecrets.length / PAGE_SIZE)
  const startIndex = (currentPage - 1) * PAGE_SIZE
  const endIndex = startIndex + PAGE_SIZE
  const secrets = filteredSecrets.slice(startIndex, endIndex)

  const handleSearchChange = (query: string) => {
    setSearchQuery(query)
    setCurrentPage(1)
  }

  const clearSearch = () => {
    setSearchQuery('')
    setCurrentPage(1)
  }

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['secrets'] })
  }

  return (
    <VStack align="stretch" gap={4}>
      <Flex justify="space-between" align="center">
        <Box position="relative" w="300px">
          <HStack
            position="absolute"
            left={3}
            top="50%"
            transform="translateY(-50%)"
            zIndex={1}
            pointerEvents="none"
          >
            <SearchIcon size={16} color="gray" />
          </HStack>
          <Input
            size="sm"
            pl={10}
            pr={searchQuery ? 10 : 3}
            placeholder="Search by name, namespace, type, or key..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {searchQuery && (
            <Button
              size="xs"
              variant="ghost"
              position="absolute"
              right={1}
              top="50%"
              transform="translateY(-50%)"
              onClick={clearSearch}
              aria-label="Clear search"
              minW="auto"
              h="auto"
              p={1}
            >
              <XIcon size={14} />
            </Button>
          )}
        </Box>
        <HStack gap={2}>
          {isAdmin && namespace && (
            <Button size="sm" colorPalette="blue" onClick={() => setShowCreateModal(true)}>
              <PlusIcon size={16} />
              Create
            </Button>
          )}
          <Button size="sm" onClick={() => refetch()}>
            <RefreshCwIcon size={16} />
            Refresh
          </Button>
        </HStack>
      </Flex>

      <Card.Root>
        <Table.Root>
          <Table.Header>
            <Table.Row bg="bg.subtle">
              <Table.ColumnHeader>Name</Table.ColumnHeader>
              <Table.ColumnHeader>Namespace</Table.ColumnHeader>
              <Table.ColumnHeader>Type</Table.ColumnHeader>
              <Table.ColumnHeader>Keys</Table.ColumnHeader>
              <Table.ColumnHeader>Age</Table.ColumnHeader>
              <Table.ColumnHeader>Actions</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {isLoading ? (
              <Table.Row>
                <Table.Cell colSpan={6}>
                  <Flex justify="center" py={8}>
                    <Spinner />
                  </Flex>
                </Table.Cell>
              </Table.Row>
            ) : secrets.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={6}>
                  <VStack py={8} gap={2}>
                    <KeyIcon size={32} color="gray" />
                    <Text textAlign="center" color="content.secondary">
                      {searchQuery
                        ? `No Secrets matching "${searchQuery}"`
                        : namespace
                          ? 'No Secrets in this namespace'
                          : 'No Secrets found'}
                    </Text>
                    {searchQuery && (
                      <Button size="sm" variant="ghost" onClick={clearSearch}>
                        Clear search
                      </Button>
                    )}
                  </VStack>
                </Table.Cell>
              </Table.Row>
            ) : (
              secrets.map((s) => (
                <Table.Row key={`${s.namespace}/${s.name}`}>
                  <Table.Cell>
                    <Text fontWeight="medium">{s.name}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge variant="outline">{s.namespace}</Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge colorPalette="purple" variant="subtle">
                      {s.type.replace('kubernetes.io/', '')}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge colorPalette="orange">{Object.keys(s.data).length}</Badge>
                  </Table.Cell>
                  <Table.Cell>{s.age}</Table.Cell>
                  <Table.Cell>
                    <HStack gap={2}>
                      <Button size="xs" variant="ghost" onClick={() => setViewSecret(s)}>
                        <EyeIcon size={14} />
                        View
                      </Button>
                      {isAdmin && (
                        <>
                          <Button
                            size="xs"
                            variant="ghost"
                            colorPalette="blue"
                            onClick={() => setEditSecret(s)}
                          >
                            <EditIcon size={14} />
                            Edit
                          </Button>
                          <Button
                            size="xs"
                            variant="ghost"
                            colorPalette="red"
                            loading={deleteMutation.isPending}
                            onClick={() => handleDelete(s.namespace, s.name)}
                          >
                            <Trash2Icon size={14} />
                            Delete
                          </Button>
                        </>
                      )}
                    </HStack>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </Card.Root>

      {/* Pagination */}
      {totalPages > 1 && (
        <Flex justify="space-between" align="center" pt={2}>
          <Text fontSize="sm" color="content.secondary">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredSecrets.length)} of{' '}
            {filteredSecrets.length} Secrets
            {searchQuery && ` (filtered from ${allSecrets.length})`}
          </Text>
          <HStack gap={2}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeftIcon size={16} />
              Previous
            </Button>
            <HStack gap={1}>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let page: number
                if (totalPages <= 5) {
                  page = i + 1
                } else if (currentPage <= 3) {
                  page = i + 1
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i
                } else {
                  page = currentPage - 2 + i
                }
                return (
                  <Button
                    key={page}
                    size="sm"
                    variant={page === currentPage ? 'solid' : 'outline'}
                    colorPalette={page === currentPage ? 'blue' : undefined}
                    onClick={() => setCurrentPage(page)}
                    minW="40px"
                  >
                    {page}
                  </Button>
                )
              })}
            </HStack>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronRightIcon size={16} />
            </Button>
          </HStack>
        </Flex>
      )}

      {/* View Modal */}
      {viewSecret && (
        <ViewSecretModal
          secret={viewSecret}
          isAdmin={isAdmin}
          onClose={() => setViewSecret(null)}
        />
      )}

      {/* Edit Modal */}
      {editSecret && (
        <SecretFormModal
          secret={editSecret}
          namespace={editSecret.namespace}
          onClose={() => setEditSecret(null)}
          onSuccess={handleSuccess}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && namespace && (
        <SecretFormModal
          namespace={namespace}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleSuccess}
        />
      )}
    </VStack>
  )
}
